// [목적] GET/PATCH /api/auth/me/onboarding/progress API 클라이언트 — 온보딩 부분 진행 조회 및 저장
// [이유] SLE-FE-03 Sub 1 — OnboardingPage 하이드레이트 + useOnboardingProgressSync 가 PATCH 를 호출.
//         컴포넌트/훅이 fetch 를 직접 호출하지 않도록 단일 모듈로 캡슐화.
//         Wave A (SLE-BE-03) 가 제공한 엔드포인트와 1:1 매핑.
// [사이드 이팩트] fetchWithEnvelope 를 경유 — 401 single-flight refresh, 403 CSRF retry, 에러 envelope 처리 모두 포함.
//                 PATCH 는 mutation 메서드 → http.ts 가 X-CSRF-Token 헤더를 자동 첨부.
//                 GET 응답 { data: null } 인 경우 null 그대로 반환 (진행 없음 상태).
// [수정 시 고려사항] OnboardingProgressOutput.snapshot 은 BE가 z.record(unknown) 으로 저장한 값 그대로.
//                   FE 는 snapshot 내부 키를 name/agreement/photo/interests 로 해석하되 strict parse 미적용.
//                   BE DTO snapshot.strict() — 미지원 키 PATCH 시 400 반환하므로 payload 는 허용 키만 포함.

import { fetchWithEnvelope } from './http.js';

// ─── FE 측 타입 선언 (BE DTO zod 추론 direct import 금지) ──────────────────────

/**
 * [목적] GET /api/auth/me/onboarding/progress 응답 data 페이로드 FE 인터페이스
 * [이유] BE onboardingProgressOutputSchema 와 키/타입 1:1 정합 — zod import 없이 FE 전용 선언
 * [사이드 이팩트] snapshot 은 BE 가 저장한 임의 JSON — FE 가 필요한 키만 접근
 * [수정 시 고려사항] updatedAt 은 ISO8601 문자열 — Date 변환 필요 시 호출처에서 처리
 */
export interface OnboardingProgressOutput {
  stepIndex: number;
  snapshot: Record<string, unknown>;
  updatedAt: string;
}

/**
 * [목적] PATCH /api/auth/me/onboarding/progress 요청 body FE 인터페이스
 * [이유] BE onboardingProgressPatchSchema 와 키/타입 1:1 정합
 * [사이드 이팩트] snapshot 내 허용 키: displayName / interests / profileImage / consents.
 *                 미지원 키 포함 시 BE 400 → useOnboardingProgressSync 에서 에러 처리 필요
 * [수정 시 고려사항] stepIndex 상한 8 (v1.0 기준). 확장 시 BE DTO 와 동기화
 */
export interface OnboardingProgressPatch {
  stepIndex: number;
  snapshot: Record<string, unknown>;
}

// ─── GET: 진행 조회 ────────────────────────────────────────────────────────────

/**
 * [목적] GET /api/auth/me/onboarding/progress — 온보딩 부분 진행 조회
 * [이유] SLE-FE-03 Sub 1 — OnboardingPage 진입 시 1회 호출하여 이전 진행 복원
 * [사이드 이팩트] data=null 이면 null 반환(진행 없음). 401 → ApiError throw (비로그인 시 호출 금지)
 * [수정 시 고려사항] JwtAuthGuard 필수 — 비로그인 상태에서 호출하면 401 → fetchWithEnvelope 가 throw
 */
export async function fetchOnboardingProgress(): Promise<OnboardingProgressOutput | null> {
  // BE envelope: { data: { stepIndex, snapshot, updatedAt } | null }
  // fetchWithEnvelope 는 body.data 를 반환. data=null 이면 null 그대로.
  const raw = await fetchWithEnvelope<OnboardingProgressOutput | null>(
    '/auth/me/onboarding/progress',
  );
  return raw ?? null;
}

// ─── PATCH: 진행 저장 ─────────────────────────────────────────────────────────

/**
 * [목적] PATCH /api/auth/me/onboarding/progress — 온보딩 부분 진행 저장 (upsert)
 * [이유] SLE-FE-03 Sub 1 — useOnboardingProgressSync 가 디바운스/즉시 flush 로 호출
 * [사이드 이팩트] CsrfGuard 필요 — http.ts 가 mutation 메서드에 X-CSRF-Token 자동 첨부.
 *                 snapshot.strict() 로 미지원 키 400 → 호출처에서 허용 키만 포함 보장 필요
 * [수정 시 고려사항] 성공 응답은 BE upsert 결과 { stepIndex, snapshot, updatedAt }
 */
export async function patchOnboardingProgress(
  payload: OnboardingProgressPatch,
): Promise<OnboardingProgressOutput> {
  const raw = await fetchWithEnvelope<OnboardingProgressOutput>('/auth/me/onboarding/progress', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  // fetchWithEnvelope 는 에러 시 throw, 성공 시 data 반환
  return raw;
}
